import React, { useContext, useRef, useState } from 'react'
import RegisterLayout from './RegisterLayout'
import ViewPdf from '../../../components/modal/ViewPdf';
import { createFullName, emptyFunction, getFileExtension, showToast } from '../../../utils';
import { MyContext } from '../../../context';
import { makeDeleteCall, makeGetCall, makePostCall } from '../../../effects/requests';
import { endPoints } from '../../../utils/endpoints';
import { ApiResponseType } from '../../../types';
import { DateUtils } from '../../../utils/dateUtils';

interface StepCrewCVProps {
    onNext: (data: any) => void;
    onPrevious: () => void;
}

const availabilityData = [
    { level: 'Publicly Available', value: 'public' },
    { level: 'Available on Request', value: 'private' },
];

const StepCrewCV = ({ onNext, onPrevious }: StepCrewCVProps) => {

    const { userData, updateUserData } = useContext(MyContext)

    const [accessType, setAccessType] = useState<'public' | 'private'>('public');
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [dragging, setDragging] = useState(false);
    const [selectedPdf, setSelectedPdf] = useState(null);

    const getUserDetails = () => {
        makeGetCall(endPoints.PROFILE, null, false, true).then((res: ApiResponseType<any>) => {
            if (res?.data?.crew_member_profile?.company_department?.length > 0) {
                res?.data?.crew_member_profile?.company_department?.map((item: any) => {
                    item.label = item?.dep;
                    item.value = item?.dep;
                })
            }
            updateUserData(res);
        }).catch(emptyFunction)
    }

    const handleFiles = (files: FileList | null) => {
        if (!files || files.length === 0) return;

        const file = files[0];

        if (file.type !== "application/pdf") {
            alert("Only PDF files are allowed");
            return;
        }

        // Optional: validate size < 1MB
        if (file.size > 1024 * 1024) {
            alert("PDF must be less than 1MB");
            return;
        }

        const fd = new FormData();
        fd.append('cv', file);
        fd.append('is_public', accessType == 'public' ? 'true' : 'false');

        makePostCall(endPoints.USER_CV, fd).then((res) => {
            if (res.status.code === 200) {
                showToast('Uploaded successfully!', 'success');
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                getUserDetails();
            }
        }).catch(emptyFunction)

    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setDragging(false);
        handleFiles(e.dataTransfer?.files);
    };

    const handleDelete = () => {
        makeDeleteCall(endPoints.USER_CV).then((res) => {
            if (res.status.code === 200) {
                showToast('Deleted successfully!', 'success');
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }
                getUserDetails();
            }
        }).catch(() => {
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        })

    }

    const handleDownload = (fileURL: any, filename: any) => {
        const pdfAcceptType = ["pdf", 'PDF'];
        const imgAcceptType = ["jpg", "jpeg", "png", "JPG", "JPEG", "PNG"];
        const common = [...imgAcceptType, ...pdfAcceptType];
        const extension = getFileExtension(fileURL);

        if (common.includes(extension)) {
            const name = createFullName(userData?.first_name, userData?.last_name);
            const aws_url = fileURL;
            const url = pdfAcceptType.includes(extension) ? endPoints.WATER_MARK : endPoints.WATER_MARK_IMAGE;
            const payload = { name, aws_url };

            makePostCall(url, payload).then((res: ApiResponseType<any>) => {
                const watermarkedURL = res?.data?.watermarked_url || fileURL;
                downloadFile(watermarkedURL, filename);
            });
        } else {
            downloadFile(fileURL, filename);
        }
    };

    const downloadFile = (fileURL: any, filename: any) => {
        // Create a virtual anchor element
        const anchor = document.createElement('a');
        anchor.href = fileURL;

        // Set the download attribute to specify the file name
        anchor.download = filename;

        // Append the anchor to the body
        document.body.appendChild(anchor);

        // Trigger a click on the anchor to initiate the download
        anchor.click();

        // Remove the anchor from the body
        document.body.removeChild(anchor);
    }

    const cvName = `${createFullName(userData?.first_name, userData?.last_name)}CV.PDF`

    return (
        <RegisterLayout showLogout hideHeader>
            <div className="register-step">
                <div className="register-step__heading-block">
                    <h1 className="register-step__title">
                        Add your CV for easy viewing on your profile
                    </h1>
                    <p className="register-step__description">
                        Only include details you’re happy to share. If you’d prefer, upload a version without contact details and keep communication in-app.
                    </p>
                </div>

                <div className="add-days-available">
                    <div className="availability-tabs">
                        {availabilityData?.map((item: any) => (
                            <button
                                key={item.value}
                                className={`availability-tab ${accessType === item?.value ? 'active' : ''}`}
                                onClick={() => {
                                    setAccessType(item?.value);
                                }}
                            >
                                {item?.level}
                            </button>
                        ))}
                    </div>

                    <div
                        className={`media-step__dropzone${dragging ? " media-step__dropzone--active" : ""}`}
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={handleDrop}
                        style={{ marginTop: 15, marginBottom: 0 }}
                    >
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".pdf,application/pdf"
                            hidden
                            onChange={(e) => handleFiles(e.target.files)}
                        />
                        <div className="media-step__dropzone-icon">
                            <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                                <rect x="2" y="8" width="36" height="26" rx="4" stroke="#a0aec0" strokeWidth="2" fill="none" />
                                <circle cx="14" cy="18" r="4" stroke="#a0aec0" strokeWidth="2" fill="none" />
                                <path d="M2 30l10-8 8 6 6-5 12 7" stroke="#a0aec0" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </div>
                        <p className="media-step__dropzone-text">
                            <span className="media-step__dropzone-link">Click to upload</span>
                            {" "}or drag and drop
                        </p>
                        <p className="media-step__dropzone-hint">PDF less than 1MB</p>
                    </div>

                    {(userData?.cv_request?.cv_exists && userData?.cv_request?.cv) && (
                        <div className="cv-file">
                            <div
                                className="cv-file__info"
                                onClick={() => setSelectedPdf(userData?.cv_request?.cv)}
                            >
                                <span className="cv-file__name">{cvName}</span>
                                <span className="cv-file__date">
                                    {`${DateUtils.formatDateTime(userData?.cv_request?.cv_updated, 'DD MMMM YYYY')}`}
                                </span>
                            </div>

                            <button
                                type="button"
                                className="cv-file__delete"
                                onClick={handleDelete}
                            >
                                <img src="/assets/images/delete-red.svg" alt="Delete" />
                            </button>
                        </div>
                    )}

                </div>

                {/* Footer */}
                <div className="register-step__footer">
                    <button
                        className="btn-next btn-next--yellow"
                        type="button"
                        style={{ marginRight: 15 }}
                        onClick={onPrevious}
                    >
                        Previous
                    </button>
                    <button
                        className="btn-next btn-next--yellow"
                        type="button"
                        onClick={onNext}
                    >
                        Next
                    </button>
                </div>
                {selectedPdf &&
                    <ViewPdf
                        onClose={() => { setSelectedPdf(null) }}
                        file={selectedPdf}
                        onDownload={() => handleDownload(selectedPdf, cvName)}
                    />
                }
            </div>
        </RegisterLayout>
    )
}

export default StepCrewCV